import lazyLoad from 'app/shared-components/util/lazyLoad';
import withContext from "app/shared-components/util/withContext";
import withClientContext from 'app/shared-components/util/withClientContext';
import {OrganizationAppContext} from "./organization/OrganizationApp";

const OrganizationsApp = lazyLoad(() => import('./OrganizationsApp'));
const OrganizationApp = lazyLoad(() => import('./organization/OrganizationApp'));
const OrganizationForm = lazyLoad(() => import('./organization/OrganizationForm'));
const OrganizationPrograms = lazyLoad(() => import('./organization/OrganizationPrograms'));
const OrganizationLocations = lazyLoad(() => import('./organization/OrganizationLocations'));
const OrganizationReferrals = lazyLoad(() => import('./organization/OrganizationReferrals'));
const OrganizationClients = lazyLoad(() => import('./organization/OrganizationClients'));
const ChannelAdmin = lazyLoad(() => import('app/shared-components/channel/channelApp/ChannelAdmin'));
const ChannelMembers = lazyLoad(() => import('app/shared-components/channel/channelApp/ChannelMembers'));
const ChannelAppTopicMembers = lazyLoad(() => import('app/shared-components/channel/channelApp/ChannelAppTopicMembers'));
const ChannelFiles = lazyLoad(() => import('app/shared-components/channel/channelApp/ChannelFiles'));
const ChannelAppChannel = lazyLoad(() => import('app/shared-components/channel/channelApp/ChannelAppChannel'));
const ChannelAppThreads = lazyLoad(() => import('app/shared-components/channel/channelApp/ChannelAppThreads'));
const ChannelAppClient = lazyLoad(() => import('app/shared-components/channel/channelApp/ChannelAppClient'));
const ChannelAppClientMembers = lazyLoad(() => import('app/shared-components/channel/channelApp/ChannelAppClientMembers'));
const TopicList = lazyLoad(() => import('app/shared-components/channel/TopicList'));
const ClientTeams = lazyLoad(() => import('app/shared-components/client/ClientTeams'));
const ClientAuthorizedUsers = lazyLoad(() => import('app/shared-components/client/ClientAuthorizedUsers'));
const RecordsApp = lazyLoad(() => import('app/shared-components/client-records/views/RecordsApp'));
const ClientTasksApp = lazyLoad(() => import('../clients/tasks/ClientTasksApp'));
const ClientInfoView = lazyLoad(() => import('app/shared-components/client/ClientInfoView'));
const ClientFiles = lazyLoad(() => import('app/shared-components/client/ClientFiles'));
const ClientServicesApp = lazyLoad(async () => {
  const { default: Component } = await import('app/shared-components/client/ClientServicesApp');
  const { default: withClientContext } = await import('app/shared-components/util/withClientContext');
  return { default: withClientContext(Component) };
});
const ClientSessionsTab = lazyLoad(() => import('app/shared-components/client/ClientSessionsTab'));
const OrganizationRecordTemplatesApp = lazyLoad(() => import('./organization/OrganizationRecordTemplatesApp'));
const SettingsApp = lazyLoad(() => import('../settings/SettingsApp'));



// Lazy load shared ManageClient and wrap with unified ClientAppContext
const ManageClient = lazyLoad(async () => {
  const { default: Component } = await import('app/shared-components/client/manage/ManageClient');
  return { default: withClientContext((props) => <Component showHeader={false} showLeftSidebar={false} {...props} />) };
});

// Lazy load the base notes component and wrap it with context
const ClientNotes = lazyLoad(async () => {
  const { default: Component } = await import('app/shared-components/client/ClientNotes');
  return { default: withContext(Component, OrganizationAppContext) };
});

const OrganizationsAppConfig = {
  settings: {
    layout: {
      config: {},
    },
  },
  helpConfig: {
    query: 'organizations',
    tag: 'organizations',
    title: 'Organizations'
  },
  routes: [
    {
      path: 'apps/organizations',
      element: <OrganizationsApp />,
      children: [
        {
          path: ':id',
          element: <OrganizationForm />,
        },
        {
          path: 'invites',
          element: <div/>,
        }
      ],
    },
    {
      path: 'apps/organization/:groupId',
      element: <OrganizationApp />,
      helpConfig: {
        query: 'organization management',
        tag: 'organizations',
        title: 'Organization Management'
      },
      children: [
        {
          path: '',
          element: <ChannelAppChannel type="organization"/>,
        },
        {
          path: 'searchresults',
          element: <ChannelAppChannel type="organization"/>,
        },
        {
          path: 'channel/:channelId',
          element: <ChannelAppChannel type="organization"/>,
        },
        {
          path: 'channel/:channelId/searchresults',
          element: <ChannelAppChannel type="organization"/>,
        },
        {
          path: 'channel/:channelId/members',
          element: <ChannelAppTopicMembers type="organization" />,
        },
        {
          path: 'client/:clientId',
          element: <ChannelAppClient type="organization" />,
        },
        {
          path: 'client/:clientId/searchresults',
          element: <ChannelAppClient type="organization" />,
        },
        {
          path: 'client/:clientId/members',
          element: <ChannelAppClientMembers type="organization" />,
        },
        {
          path: 'clients',
          element: <OrganizationClients/>,
          helpConfig: {
            query: 'organization client management',
            title: 'Organization Clients'
          }
        },
        {
          path: 'clients/:clientId',
          element: <OrganizationClients/>,
        },
        {
          path: 'manageclient/:clientId',
          element: <ManageClient/>,
          helpConfig: {
            query: 'organization client management',
            title: 'Organization Clients'
          },
          children: [
            {
              path: '',
              element: <ClientInfoView />,
            },
            {
              path: 'records/insurance',
              element: <RecordsApp displayMode="insurance" />,
            },
            {
              path: 'records/providers',
              element: <RecordsApp displayMode="providers" />,
            },
            {
              path: 'records/diagnosis',
              element: <RecordsApp displayMode="diagnosis" />,
            },
            {
              path: 'records/template/:templateId',
              element: <RecordsApp />,
              helpConfig: {
                query: 'client record management',
                title: 'Client Record Management'
              },
            },
            {
              path: 'teams',
              element: <ClientTeams />,
            },
            {
              path: 'teams/view/:teamId',
              element: <ClientTeams />,
            },
            {
              path: 'teams/add/view/:teamId',
              element: <ClientTeams />,
            },
            {
              path: 'teams/add',
              element: <ClientTeams />,
            },
            {
              path: 'users',
              element: <ClientAuthorizedUsers />,
            },
            {
              path: 'users/view/:userId',
              element: <ClientAuthorizedUsers />,
            },
            {
              path: 'users/chat/:userId',
              element: <ClientAuthorizedUsers />,
            },
            {
              path: 'users/pending',
              element: <ClientAuthorizedUsers />,
            },
            {
              path: 'users/pending/view/:userId',
              element: <ClientAuthorizedUsers />,
            },
            {
              path: 'users/add',
              element: <ClientAuthorizedUsers />,
            },
            {
              path: 'users/add/view/:userId',
              element: <ClientAuthorizedUsers />,
            },
            {
              path: 'users/addmember',
              element: <ClientAuthorizedUsers />,
            },
            {
              path: 'users/addmember/view/:userId',
              element: <ClientAuthorizedUsers />,
            },
            {
              path: 'records',
              element: <RecordsApp displayMode="templated" />,
              helpConfig: {
                query: 'client record management',
                title: 'Client Record Management'
              },
            },
            {
              path: 'tasks',
              element: <ClientTasksApp />,
              helpConfig: {
                query: 'client tasks',
                title: 'Client Tasks'
              },
            },
            {
              path: 'sessions',
              element: <ClientSessionsTab />,
              helpConfig: {
                query: 'client sessions',
                title: 'Client Sessions'
              },
            },
            {
              path: 'files',
              element: <ClientFiles />,
            },
            {
              path: 'files/:folderId',
              element: <ClientFiles />,
            },
          ],
        },
        {
          path: 'programs',
          element: <OrganizationPrograms/>,
          helpConfig: {
            query: 'organization programs',
            tag: 'organizations',
            title: 'Organization Programs'
          }
        },
        {
          path: 'program/:programId',
          element: <OrganizationPrograms/>,
          helpConfig: {
            query: 'organization programs',
            tag: 'organizations',
            title: 'Organization Programs'
          }
        },
        {
          path: 'program/:programId/locations',
          element: <OrganizationPrograms/>,
          helpConfig: {
            query: 'organization programs',
            tag: 'organizations',
            title: 'Organization Programs'
          }
        },
        {
          path: 'locations',
          element: <OrganizationLocations/>,
          helpConfig: {
            query: 'organization locations',
            tag: 'organizations',
            title: 'Organization Locations'
          }
        },
        {
          path: 'location/:locationId',
          element: <OrganizationLocations/>,
          helpConfig: {
            query: 'organization locations',
            tag: 'organizations',
            title: 'Organization Locations'
          }
        },
        {
          path: 'location/:locationId/programs',
          element: <OrganizationLocations/>,
          helpConfig: {
            query: 'organization locations',
            tag: 'organizations',
            title: 'Organization Locations'
          }
        },
        {
          path: 'referrals',
          element: <OrganizationReferrals/>,
          helpConfig: {
            query: 'organization referrals',
            title: 'Organization Referrals'
          }
        },
        {
          path: 'referrals/:tab',
          element: <OrganizationReferrals/>,
          helpConfig: {
            query: 'organization referrals',
            title: 'Organization Referrals'
          }
        },
        {
          path: 'referral/:referralId',
          element: <OrganizationReferrals/>,
          helpConfig: {
            query: 'organization referrals',
            title: 'Organization Referrals'
          }
        },
        {
          path: 'topics',
          element: <TopicList type="organization"/>,
        },
        {
          path: 'threads',
          element: <ChannelAppThreads type="organization"/>,
        },
        {
          path: 'topic/:topicId',
          element: <TopicList type="organization"/>,
        },
        {
          path: 'files',
          element: <ChannelFiles type="organization" />,
        },
        {
          path: 'files/view/:fileId',
          element: <ChannelFiles type="organization" />,
        },
        {
          path: 'files/:folderId',
          element: <ChannelFiles type="organization" />,
        },
        {
          path: 'files/:folderId/view/:fileId',
          element: <ChannelFiles type="organization" />,
        },
        {
          path: 'admin',
          element: <ChannelAdmin type="organization" formElement={OrganizationForm}/>,
          helpConfig: {
            query: 'organization administration',
            tag: 'organizations',
            title: 'Organization Administration'
          }
        },
        {
          path: 'members',
          element: <ChannelMembers type="organization"/>,
        },
        {
          path: 'member/:memberId',
          element: <ChannelMembers type="organization"/>,
        },
        {
          path: 'result/:userId',
          element: <ChannelMembers type="organization"/>,
        },
        {
          path: 'templates',
          element: <OrganizationRecordTemplatesApp />,
          helpConfig: {
            query: 'organization record templates',
            title: 'Record Templates'
          }
        },
        {
          path: 'services',
          element: <ClientServicesApp />,
          helpConfig: {
            query: 'organization client services',
            title: 'Client Services'
          }
        },
        {
          path: 'settings/:configType?',
          element: <SettingsApp />,
          helpConfig: {
            query: 'organization settings',
            title: 'Organization Settings'
          }
        },
      ],
    },
  ],
};

export default OrganizationsAppConfig;
